import React from 'react';
import { CheckIcon } from 'lucide-react';
import { ProductImage } from './ProductImage';
import { PRODUCT_NAME, PILLARS } from './constants';
const includes = ['Ready-to-teach lesson structures', 'Organized PE activities and games', 'Skill progressions and drills', 'Fitness and movement sessions', 'Group and class-management tools', 'Adaptations for different levels', 'Assessment and progress resources'];
const modules = [{
  num: '1',
  title: 'PE LESSON PLANNING & TEACHING SYSTEM™',
  paragraphs: ['Access ready-to-use lesson structures for 30-, 45- and 60-minute classes, indoor and outdoor sessions, stations, circuits, partner activities and team formats.', 'Includes practical planning tools, preparation checklists and adaptations for different groups and teaching situations.'],
  kicker: 'Choose the structure. Prepare. Teach.'
}, {
  num: '2',
  title: 'READY-TO-TEACH PE ACTIVITY LIBRARY™',
  paragraphs: ['Access organized games, movement activities, coordination tasks, agility work, relays, partner activities, team challenges, stations and low- or no-equipment options.', 'Each activity includes setup, instructions, teaching cues, progressions, regressions and practical variations.'],
  kicker: 'Complete activities. Ready to teach.'
}, {
  num: '3',
  title: 'FUNDAMENTAL MOVEMENT & SPORTS SKILLS™',
  paragraphs: ['Teach running, jumping, balance, agility, throwing, catching, kicking, passing, striking, dribbling and coordination through clear progressions.', 'Also includes practical applications for common sports and PE activities.'],
  kicker: 'Teach the skill. Progress the student.'
}, {
  num: '4',
  title: 'FITNESS, CONDITIONING & MOVEMENT SESSIONS™',
  paragraphs: ['Access ready-to-use cardiovascular activities, bodyweight strength work, mobility, agility, coordination, balance, fitness circuits and team challenges.', 'Includes easier and more challenging variations for different groups.'],
  kicker: 'Choose the session. Adjust the level. Teach.'
}, {
  num: '5',
  title: 'CLASS MANAGEMENT & INCLUSIVE PE TOOLKIT™',
  paragraphs: ['Use practical systems for groups, teams, stations, equipment, transitions, participation and mixed-ability classes.', 'Adapt rules, space, equipment and difficulty without rebuilding the entire activity.'],
  kicker: 'Better flow. Less waiting. More participation.'
}, {
  num: '6',
  title: 'ASSESSMENT, PROGRESS TRACKING & PE RESOURCES™',
  paragraphs: ['Access observation sheets, rubrics, participation tools, progress trackers, student records, peer assessment, self-assessment and lesson-reflection resources.', 'Assess and track progress without starting from zero.'],
  kicker: ''
}];
export function SystemSection() {
  return <section className="bg-white px-4 py-6 border-b border-brand-line" data-id="element-142" data-mp="35poy247zt4je">
      <div className="max-w-4xl mx-auto" data-id="element-143" data-mp="3d8g3eenlhkpz">
        <h2 className="font-display text-2xl sm:text-3xl text-brand-navy text-center leading-[1.05] mb-3" data-id="element-144" data-mp="3d7mr6y5va30g">
          The Complete 6-in-1 PE Teaching System
        </h2>

        <div className="bg-brand-navy rounded-xl p-4 text-center mb-3" data-id="element-145" data-mp="3myr5dvnpxju8">
          <p className="font-display text-2xl sm:text-3xl text-white leading-none" data-id="element-146" data-mp="3rwj8llttcsdq">
            🏃 {PRODUCT_NAME}
          </p>
          <p className="font-heading font-semibold text-xs sm:text-sm text-brand-orange uppercase tracking-widest mt-1" data-id="element-147" data-mp="2ua3qqixh5spn">
            The Complete Ready-to-Teach System
          </p>
          <p className="text-[11px] sm:text-xs text-white/80 font-medium mt-1" data-id="element-148" data-mp="346oul0xrcxrw">
            {PILLARS}
          </p>
        </div>

        <ul className="grid sm:grid-cols-2 lg:grid-cols-3 gap-x-4 gap-y-1 mb-5" data-id="element-149" data-mp="3972yq7w8nnkn">
          {includes.map(item => <li key={item} className="flex items-start gap-1.5 text-sm text-brand-ink font-medium leading-snug" data-id="element-150" data-mp="jj3llbeikl24">
              <CheckIcon className="w-4 h-4 text-brand-green shrink-0 mt-0.5" aria-hidden="true" data-id="element-151" data-mp="m0nkbq3pm83m" />
              {item}
            </li>)}
        </ul>

        <h3 className="font-display text-xl sm:text-2xl text-brand-navy text-center mb-3" data-id="element-152" data-mp="w0mpre4u1ic9">
          What's Inside
        </h3>

        <ol className="grid sm:grid-cols-2 gap-3" data-id="element-153" data-mp="qz771sq4nnx1">
          {modules.map(m => <li key={m.num} className="h-full" data-id="element-154" data-mp="14yqcuevgun66">
              <div className="h-full bg-brand-cream border border-brand-line rounded-xl p-3.5 flex flex-col shadow-sm" data-id="element-155" data-mp="10yy0rs6v5yw2">
                <div className="flex items-start gap-2.5 mb-1.5" data-id="element-156" data-mp="1eqck604oo23n">
                  <span className="bg-brand-green text-white font-display text-lg w-8 h-8 flex items-center justify-center rounded-lg shrink-0" data-id="element-157" data-mp="19qzz9kxdh53p">
                    {m.num}
                  </span>
                  <h4 className="font-heading font-extrabold text-[13px] sm:text-sm text-brand-navy leading-tight uppercase" data-id="element-158" data-mp="1jp5zly3j636g">
                    ✅ Module {m.num} — {m.title}
                  </h4>
                </div>
                {m.paragraphs.map(p => <p key={p} className="text-xs sm:text-[13px] text-brand-slate font-medium leading-snug mb-1.5" data-id="element-159" data-mp="27c17x8ijcs5o">
                    {p}
                  </p>)}
                {m.kicker && <p className="font-heading font-bold text-xs text-brand-green uppercase tracking-wide mt-auto" data-id="element-160" data-mp="1jmqbu4c4rlxv">
                    {m.kicker}
                  </p>}
              </div>
            </li>)}
        </ol>

        <div className="mt-4" data-id="element-161" data-mp="1ak7d9wfdnvhf">
          <ProductImage className="max-w-[220px]" data-id="element-162" data-mp="1fl54eyid7zv2" />
        </div>
      </div>
    </section>;
}